import { createElement as rc } from 'react';
import icons from '../customIcons2';
import iconsOld from '../customIcons';
import { iconStyle } from '../../styling/themes/gl2025/icons';
import logging from '@sstdev/lib_logging';
import PropTypes from 'prop-types';
import styled from '../../styled';
import { css } from 'styled-components';
import { styleObjectToCss } from '../../styling/utilities';


const StyledSvg = styled('svg').attrs(props => {
    const { size } = props;
    const { svg: svgStyle } = iconStyle[size];
    return { svgStyle };
})`
    flex-shrink: 0;
    ${({ svgStyle, fillColor }) => css`
        ${styleObjectToCss({
            ...svgStyle,
            fill: fillColor
        })}
    `}
`;

const StyledPath = styled('path').attrs(props => {
    const { size } = props;
    const { path: pathStyle } = iconStyle[size];
    return { pathStyle };
})`
    ${({ pathStyle, origProps, theme }) => css`
        ${styleObjectToCss({
            ...pathStyle,
            stroke: getIconColor({ ...origProps, theme})
        })}
    `}
`;

// TODO gl2025 remove MaterialIcon below and associated code in BaseIcon once old style is no longer supported
const MaterialIcon = styled('i').attrs(props => {
    const iconParts = props.children?.split('|') ?? [];
    const transform = iconParts?.[1] ?? 'unset';
    const className =
        (props.theme.iconFont || props.theme.icon?.font) + (props.iconVariation ? '-' + props.iconVariation : '');
    return { className, transform, children: iconParts[0] };
})`
    font-size: ${props => (props.iconSize ?? props.theme.iconSize) + 'px'} !important;
    color: ${({ origProps, theme }) => getIconColor({ ...origProps, theme }) };
    transform: ${props => props.transform};
    user-select: none;
`;
MaterialIcon.displayName = 'MaterialIcon';

export default function BaseIcon(props) {
    const { name, color, size, fill = false, 'data-testid': dataTestId, children } = props;

    const fillColor = fill ? color : 'none';

    let d, iconSize;
    if (!Object.keys(icons).includes(name)) {
        logging.warn(`No icon found with name ${name} in customIcons2.js. Trying customIcons.js instead.`);
        d = iconsOld[name];
        iconSize = 'md';
    } else {
        d = icons[name];
        iconSize = size;
    }

    if (d) {
        return rc(
            StyledSvg,
            { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', size: iconSize, fillColor, 'data-testid': dataTestId },
            rc(StyledPath, { d, size: iconSize, origProps: props })
        );
    } else {
        logging.warn(`No icon found with name ${name} in customIcons.js. Using MaterialIcon instead.`);
        // when passing in all props, something was causing a weird transformation issue
        // so only pass in what we actually need
        return rc(MaterialIcon, { children, origProps: props, iconSize: iconStyle[size].svg.height, 'data-testid': dataTestId });
    }
}

BaseIcon.propTypes = {
    name: PropTypes.string.isRequired, // must be one of the icon names in customIcons2.js
    color: PropTypes.string, // this is the hex color itself (e.g. #ff0000), not something like 'warning'
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']).isRequired,
    fill: PropTypes.bool // whether to fill in the icon; generally going to be false
};

BaseIcon.defaultProps = {
    size: 'md',
    fill: false
};

function getIconColor(props) {
    const { color, disabled, disabledColor, fontColor, theme } = props;

    if (color === 'currentColor') return color;
    if (disabled) return disabledColor ?? theme.disabledFontColor;
    if (fontColor) return fontColor;
    if (['base', 'transparent'].includes(color)) return theme.defaultFontColor;
    return color || theme.defaultFontColor;
}
